import { isAxiosError } from 'axios';
import { stanNotify } from '@/lib';

export type ErrorType = 'network' | 'timeout' | 'rate_limit' | 'server_error' | 'validation' | 'api' | 'unexpected';

export interface ApiErrorResult {
    type: ErrorType;
    message: string;
    /** Validation errors mapped by field name (API field names, e.g., 'phone_number') */
    fieldErrors?: Record<string, string[]>;
    /** HTTP status code if available */
    status?: number;
}

/**
 * Parses an API error and returns a structured result with error type and message.
 * This is a pure function that doesn't have side effects - it just analyzes the error.
 */
export function parseApiError(error: unknown): ApiErrorResult {
    if (!isAxiosError(error)) {
        return {
            type: 'unexpected',
            message: error instanceof Error ? error.message : 'Unknown error',
        };
    }

    const status = error.response?.status;

    // Network error (no response received)
    if (!error.response) {
        return {
            type: 'network',
            message: 'Network error - no response',
        };
    }

    // Request timeout
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        return {
            type: 'timeout',
            message: 'Request timed out',
            status,
        };
    }

    // Rate limited (429)
    if (status === 429) {
        return {
            type: 'rate_limit',
            message: 'Too many requests',
            status,
        };
    }

    // Server error (5xx)
    if (status && status >= 500) {
        return {
            type: 'server_error',
            message: `Server error: ${status}`,
            status,
        };
    }

    // Validation errors from API
    const validationErrors = error.response?.data?.errors;
    if (validationErrors && typeof validationErrors === 'object') {
        return {
            type: 'validation',
            message: 'Form validation errors',
            fieldErrors: validationErrors as Record<string, string[]>,
            status,
        };
    }

    // Generic API error
    return {
        type: 'api',
        message: error.response?.data?.message || error.message || 'Request failed',
        status,
    };
}

/**
 * User-friendly error messages for each error type.
 * These are suitable for displaying in toast notifications.
 */
export const ERROR_MESSAGES: Record<ErrorType, { title: string; text: string }> = {
    network: {
        title: 'Connection Error',
        text: 'Unable to connect to the server. Please check your internet connection and try again.',
    },
    timeout: {
        title: 'Request Timeout',
        text: 'The request took too long. Please try again.',
    },
    rate_limit: {
        title: 'Too Many Attempts',
        text: 'Please wait a moment before trying again.',
    },
    server_error: {
        title: 'Server Error',
        text: 'Our servers are experiencing issues. Please try again in a few moments.',
    },
    validation: {
        title: 'Validation Error',
        text: 'Please check the form for errors.',
    },
    api: {
        title: 'Request Failed',
        text: 'Something went wrong. Please try again.',
    },
    unexpected: {
        title: 'Unexpected Error',
        text: 'An unexpected error occurred. Please try again.',
    },
};

/**
 * Shows a toast notification for an API error.
 * Uses the error type to determine the appropriate message.
 */
export function notifyApiError(errorResult: ApiErrorResult, customTitle?: string): void {
    const defaultMessage = ERROR_MESSAGES[errorResult.type];

    stanNotify({
        title: customTitle || defaultMessage.title,
        // For API errors, use the actual error message if available
        text: errorResult.type === 'api' ? errorResult.message : defaultMessage.text,
        type: 'error',
    });
}

/**
 * Convenience function that parses an error and shows a notification.
 * Returns the parsed error result for further handling (e.g., setting form errors).
 *
 * @example
 * // Basic usage - just show a notification
 * try {
 *     await api.post('/endpoint', data);
 * } catch (error) {
 *     handleApiError(error);
 * }
 *
 * @example
 * // With custom title
 * try {
 *     await api.post('/endpoint', data);
 * } catch (error) {
 *     handleApiError(error, 'Registration Failed');
 * }
 *
 * @example
 * // With form validation error handling (React Hook Form)
 * try {
 *     await api.post('/register', data);
 * } catch (error) {
 *     const result = handleApiError(error, 'Registration Failed');
 *
 *     // Map API field errors to form fields
 *     if (result.type === 'validation' && result.fieldErrors) {
 *         const fieldMap: Record<string, keyof FormValues> = {
 *             phone_number: 'phoneNumber',
 *             full_name: 'fullName',
 *         };
 *
 *         Object.entries(result.fieldErrors).forEach(([field, messages]) => {
 *             const formField = fieldMap[field] || field;
 *             if (messages[0]) {
 *                 form.setError(formField, { type: 'server', message: messages[0] });
 *             }
 *         });
 *     }
 * }
 *
 * @example
 * // With analytics tracking
 * try {
 *     await api.post('/register', data);
 * } catch (error) {
 *     const result = handleApiError(error, 'Registration Failed');
 *     trackFailure(result.type, result.message);
 * }
 */
export function handleApiError(error: unknown, customTitle?: string): ApiErrorResult {
    const result = parseApiError(error);
    notifyApiError(result, customTitle);
    return result;
}
